/**
 * authentication.ts
 * desc: helper utility for authentication
 * note: includes common operations such as logout
 */
import type Keycloak from 'keycloak-js';
import { getItem } from './local-storage';


/**
 * hasAccess
 * desc: check route permissions against users permissions
 * notes:
 *   - null permissions means unprotected route (distinct from the [] default,
 *     which protects the route and matches nobody)
 *   - required: user must have ALL of these (every)
 *   - permissions: user must have at least ONE of these (some)
 */
export function hasAccess(permissions: string[] | null = [], userPermissions: string[] = [], required: string[] = []): boolean {
    // null means unprotected route
    if (permissions === null) return true;

    const hasRequired = required.every(p => userPermissions.includes(p));
    return hasRequired && _checkForSomeInArray(permissions, userPermissions);
}


/**
 * logout
 * desc: logout using the keycloak function
 * note: user will be redirected to the login page
 */
export function logout(): void {
    keycloak!.logout();
}


/**
 * setKeycloak
 * desc: save the keycloak instance
 * notes: we need to have this instance saved to use it outside of react lifecycle
 */
export function setKeycloak(newKeycloak: Keycloak): void {
    keycloak = newKeycloak;
}



/**
 * updateKeycloakToken
 * desc: update access token
 * notes: if refresh token is expired need to logout the user with error message
 */
export function updateKeycloakToken(): Promise<boolean | void> {
    const TWO_MINUTES = 2 * 60;
    if (isRefreshTokenExpired()) {
        return logoutDueToInactivity();
    } else {
        // refresh token within min validity of 2min
        // note: keycloak-js throws when a refresh fails. We split the failure
        // into two buckets:
        //   - transient (Keycloak 5xx, or a network failure surfacing as a
        //     TypeError): recoverable, so re-throw and let the caller decide.
        //   - everything else: the session can no longer be refreshed — a 400
        //     invalid_grant (ended/invalid SSO session), a locally expired
        //     refresh token, or keycloak-js throwing "no refresh token
        //     available" because it holds no refresh token at all. None of
        //     these are recoverable, so log out cleanly instead of letting the
        //     rejection escape as an unhandled promise rejection.
        return keycloak!.updateToken(TWO_MINUTES)
            .catch((error) => {
                const status = error?.response?.status;
                const isTransient = status >= 500 || error?.name === 'TypeError';
                if (isTransient) {
                    throw error;
                }
                return logoutDueToInactivity();
            });
    }
}



/**
 * isRefreshTokenExpired
 * desc: check if keycloak refresh token expired
 * note: parseInt stringifies its argument at runtime anyway — String() only
 *       makes that coercion visible to the compiler
 */
export function isRefreshTokenExpired(): boolean {
    const exp = Number.parseInt(String(getItem('refreshTokenExp')));
    const now = Number.parseInt(String(Date.now() / 1000));
    return now >= exp;
}



/**
 * logoutDueToInactivity
 * desc: logout and flag the login page to show the inactivity message
 */
export function logoutDueToInactivity(): Promise<void> {
    return keycloak!.logout({
        redirectUri: `${window.location.href}?showInactiveMessage=true`
    });
}



//*************************************************
//*****   Helper Functions   **********************
//*************************************************
/**
 * _checkForSomeInArray
 * desc: helper function to check whether at least one element of the sub array is a part of main array
 */
function _checkForSomeInArray(subArray: string[], mainArray: string[]): boolean {
    if (!subArray || !Array.isArray(subArray)) throw new Error('subArray is not defined');
    if (!mainArray || !Array.isArray(mainArray)) throw new Error('mainArray is not defined');

    return subArray.some(val => mainArray.includes(val));
}


// keycloak instance — set once by the Keycloak provider during boot; the
// non-null assertions at the call sites encode that initialization order
let keycloak: Keycloak | null = null;

/**
 * getKeycloak
 * desc: read access to the module-held keycloak instance
 * note: a getter instead of exporting the mutable let-binding (Sonar S6861);
 *       reads happen at call time, identical to the old live binding
 */
export function getKeycloak(): Keycloak | null {
    return keycloak;
}
